<template>
  <PAccordion
    :version="2"
    :label="`${list.length} ${plural('signature', list.length)} à apposer`"
    v-model:open="preposOpen"
    :contentClass="`border-x border-grey-850 max-h-[${short ? '250px' : '400px'}] overflow-y-scroll`"
  >
    <div class="max-w-[250px] mb-1">
      {{ list.length }} {{ plural("pré-positionnement", list.length) }} de signature :
    </div>

    <ul>
      <li v-for="{ pageIndex, signPos } in pages" :key="pageIndex" class="flex flex-row justify-between items-center">
        <button type="button" @click="goToPage(pageIndex)" class="group pl-2">
          Page {{ pageIndex + 1 }}
          <PTooltip
            :label="`${signPos.length} ${replacePlural(signPos.length, 'signature prépositionnée', 'signatures prépositionnées')}`"
            :offset="5"
            :delay="500"
            class="ml-1 inline-block"
          >
            <div
              class="rounded-full px-1 py-0.5 mr-1 flex bg-green-tilleul-verveine-925 group-hover:bg-green-tilleul-verveine-925-hover"
            >
              <span class="text-sm">{{ signPos.length }}</span>
              <OhVueIcon scale="1" :name="postItIcon" class="mt-1v" />
            </div>
          </PTooltip>
        </button>
        <button type="button" @click="deletePosPage(pageIndex)">
          <PTooltip :label="`Supprimer signature(s) prépositionnée(s) page ${pageIndex + 1}`" :offset="5" :delay="500">
            <div class="p-1 rounded-full text-blue-france-sun-113 hover:text-white hover:bg-blue-france-sun-113-hover">
              <OhVueIcon scale="1" :name="deleteIcon" />
            </div>
          </PTooltip>
        </button>
      </li>
    </ul>

    <PButton
      v-if="pages.length > 1"
      type="button"
      mode="tertiary"
      color="error"
      size="sm"
      @click="deleteAllSignPositions"
      class="mt-2 w-full"
    >
      <OhVueIcon :name="deleteIcon" scale="0.8" class="mr-1" />
      Tout supprimer
    </PButton>
  </PAccordion>
</template>

<script setup lang="ts">
import { plural, replacePlural } from "@/app/utils/plural.ts";
import { AbstractSignObject } from "@/app/views/PdfViewer/domain/abstract.sign-object.domain.ts";
import type { SignPosition } from "@/app/views/PdfViewer/domain/sign-position.domain.ts";
import { PAccordion, PButton, PTooltip, useIcons } from "@pasta/box-v2";
import { sortBy } from "lodash";
import { OhVueIcon } from "oh-vue-icons";
import { RiDeleteBin6Line, RiStickyNoteLine } from "oh-vue-icons/icons";
import { computed, ref } from "vue";
import { useSignEvents } from "../composable/use-sign-events.ts";

const [postItIcon, deleteIcon] = useIcons(RiStickyNoteLine, RiDeleteBin6Line);

type Props = {
  list: SignPosition[];
  short?: boolean;
};
const props = withDefaults(defineProps<Props>(), { short: false });

const preposOpen = ref<boolean>(false);

const pages = computed(() => {
  const posPerPage: Map<number, SignPosition[]> = props.list.reduce((acc, curr) => {
    const pageIndex = curr.coordinates.pageIndex;
    acc.set(pageIndex, [...(acc.get(pageIndex) ?? []), curr]);
    return acc;
  }, new Map());

  return sortBy(
    Array.from(posPerPage.entries()).map(([pageIndex, signPos]) => ({
      pageIndex: +pageIndex,
      signPos,
    })),
    "pageIndex"
  );
});
const signEvents = useSignEvents();

function goToPage(pageIndex: number) {
  const page = pages.value.find(page => page.pageIndex === pageIndex)!;
  const firstSignPosition = AbstractSignObject.sort(page.signPos)[0];
  firstSignPosition.scrollTo();
}

function deletePosPage(pageIndex: number) {
  signEvents.emit("deleteObjects", "signPos", pageIndex);
}

function deleteAllSignPositions() {
  signEvents.emit("deleteObjects", "signPos");
}
</script>
